import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MainNavigation } from '../main/MainNavigation';
import { 
  Check, 
  X,
  Crown,
  Heart,
  Sparkles,
  Zap,
  Shield,
  BookOpen,
  BarChart3,
  ArrowRight,
  Play
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

type BillingInterval = 'monthly' | 'yearly';

export function PricingPage() {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const redirectToApp = useAuthStore((state) => state.redirectToApp);

  const handleSignupRedirect = (e: React.MouseEvent, plan?: string) => {
    e.preventDefault();
    const url = plan ? `/signup?plan=${plan}` : '/signup';
    redirectToApp(url);
  };

  const handleAppRedirect = (e: React.MouseEvent) => {
    e.preventDefault();
    redirectToApp('/');
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-menioo-cream">
      <MainNavigation />

      {/* 1. HERO */}
      <section className="relative bg-gradient-to-br from-menioo-cream via-white to-menioo-cream py-16 md:py-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-menioo-green/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center bg-menioo-green/10 text-menioo-green-dark px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4 mr-2" />
              Commencez gratuitement, évoluez à votre rythme
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-menioo-navy leading-tight mb-6">
              Des tarifs <span className="text-menioo-green">simples et honnêtes</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto">
              Menioo est gratuit pour démarrer. Passez à Premium pour débloquer tout le potentiel, ou soutenez-nous avec un don.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-full p-1.5 shadow-md inline-flex border border-gray-100">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-menioo-green text-white shadow-sm'
                    : 'text-gray-600 hover:text-menioo-navy'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  billingInterval === 'yearly'
                    ? 'bg-menioo-green text-white shadow-sm'
                    : 'text-gray-600 hover:text-menioo-navy'
                }`}
              >
                Annuel
                <span className="ml-2 text-xs bg-menioo-orange text-white px-2 py-0.5 rounded-full">
                  -20%
                </span>
              </button>
            </div>
          </div>

          {/* PRICING CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            
            {/* ─── FREE PLAN ─── */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100 hover:border-menioo-green/30 transition-all hover:shadow-xl">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-2xl mb-4">
                  <Sparkles className="h-7 w-7 text-gray-600" />
                </div>
                <h3 className="text-2xl font-display font-bold text-menioo-navy mb-2">
                  Gratuit
                </h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-5xl font-bold text-menioo-navy">0€</span>
                </div>
                <p className="text-sm text-gray-500 font-medium">Pour toujours, sans engagement</p>
              </div>

              <div className="bg-menioo-cream/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-menioo-navy text-center font-medium">
                  Parfait pour découvrir et commencer à s'organiser
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    <strong>Création illimitée</strong> de recettes publiques
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Parcourez des <strong>milliers de recettes</strong> de la communauté
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    <strong>10 recettes privées</strong> par recette publique publiée
                    <span className="block text-xs text-gray-500 mt-1">
                      Ex : 3 publiques = 30 privées
                    </span>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Planification sur <strong>1 semaine</strong>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Listes de courses <strong>manuelles</strong>
                    <span className="block text-xs text-gray-500 mt-1">
                      Ajoutez vos recettes une par une
                    </span>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Recherche avancée avec <strong>tags et filtres</strong>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Publicités ciblées et utiles
                    <span className="block text-xs text-gray-500 mt-1">
                      Produits et recettes sponsorisés
                    </span>
                  </span>
                </li>
              </ul>

              {isAuthenticated ? (
                <a
                  href="https://app.menioo.com"
                  onClick={handleAppRedirect}
                  className="block w-full bg-gray-100 text-menioo-navy py-4 px-6 rounded-xl text-center font-semibold hover:bg-gray-200 transition-all"
                >
                  Accéder à l'app
                </a>
              ) : (
                <a
                  href="https://app.menioo.com/signup"
                  onClick={(e) => handleSignupRedirect(e, 'free')}
                  className="block w-full bg-gray-100 text-menioo-navy py-4 px-6 rounded-xl text-center font-semibold hover:bg-gray-200 transition-all"
                >
                  Commencer gratuitement
                </a>
              )}
            </div>

            {/* ─── PREMIUM PLAN ─── */}
            <div className="bg-gradient-to-br from-menioo-green to-menioo-green-dark rounded-2xl shadow-2xl p-8 border-2 border-menioo-green transform md:scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-menioo-orange text-white px-5 py-1.5 rounded-full text-sm font-bold shadow-lg inline-flex items-center">
                  <Zap className="h-3.5 w-3.5 mr-1.5" />
                  Bientôt disponible
                </span>
              </div>

              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl mb-4">
                  <Crown className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold text-white mb-1">
                  Premium
                </h3>
                <p className="text-white/70 text-sm mb-4">L'expérience complète</p>
                <div className="flex items-baseline justify-center mb-2">
                  {billingInterval === 'monthly' ? (
                    <>
                      <span className="text-5xl font-bold text-white">9,99€</span>
                      <span className="text-white/70 ml-2 text-lg">/mois</span>
                    </>
                  ) : (
                    <>
                      <span className="text-5xl font-bold text-white">7,99€</span>
                      <span className="text-white/70 ml-2 text-lg">/mois</span>
                    </>
                  )}
                </div>
                {billingInterval === 'yearly' && (
                  <p className="text-sm text-white/70">
                    Soit 95,88€/an au lieu de <span className="line-through">119,88€</span>
                  </p>
                )}
              </div>

              <div className="bg-white/10 rounded-xl p-4 mb-6">
                <p className="text-sm text-white text-center font-medium">
                  Tout le Gratuit + les super-pouvoirs culinaires
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white">
                    <strong>Recettes privées illimitées</strong> — vos secrets culinaires
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white">
                    <strong>Planification illimitée</strong> — aussi loin que vous voulez
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white">
                    <strong>Listes de courses intelligentes</strong> — générées automatiquement
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white">
                    <strong>Vues personnalisées</strong> — filtres favoris sauvegardés
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white">
                    <strong>Zéro publicité</strong> ou choix des pubs qui vous intéressent
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white">
                    <strong>Assistant IA personnel</strong> — suggestions adaptées
                  </span>
                </li>
                
                <li className="pt-2 border-t border-white/20">
                  <div className="flex items-start">
                    <Zap className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Fonctionnalités IA :</span>
                  </div>
                  <ul className="mt-3 space-y-2.5 ml-8">
                    <li className="flex items-start">
                      <Play className="h-4 w-4 text-white/70 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm">
                        Import depuis <strong>TikTok & Instagram</strong> — vidéos en recettes
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-white/70 mr-2 flex-shrink-0 mt-0.5 text-sm">🌐</span>
                      <span className="text-white/90 text-sm">
                        Import depuis <strong>n'importe quel site</strong> — Marmiton, 750g...
                      </span>
                    </li>
                    <li className="flex items-start">
                      <BookOpen className="h-4 w-4 text-white/70 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm">
                        Import depuis <strong>vos livres de cuisine</strong> <em className="text-white/60">(prochainement)</em>
                      </span>
                    </li>
                  </ul>
                </li>

                <li className="pt-2 border-t border-white/20">
                  <div className="flex items-start">
                    <BarChart3 className="h-5 w-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">
                      Statistiques & insights <em className="text-white/60 text-xs">(prochainement)</em>
                    </span>
                  </div>
                  <ul className="mt-3 space-y-1.5 ml-8 text-sm text-white/80">
                    <li className="flex items-center"><span className="mr-2">📊</span> Analyses nutritionnelles personnalisées</li>
                    <li className="flex items-center"><span className="mr-2">🏆</span> Vos recettes stars et tendances</li>
                    <li className="flex items-center"><span className="mr-2">📈</span> Suivi de votre équilibre alimentaire</li>
                    <li className="flex items-center"><span className="mr-2">💡</span> Insights sur vos habitudes culinaires</li>
                  </ul>
                </li>
              </ul>

              <button
                disabled
                className="block w-full bg-white/20 text-white py-4 px-6 rounded-xl text-center font-semibold cursor-not-allowed border border-white/20"
              >
                Bientôt disponible
              </button>
              <p className="text-center text-white/70 text-xs mt-3">
                Inscrivez-vous gratuitement pour être notifié du lancement
              </p>
            </div>

            {/* ─── DONATION PLAN ─── */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-amber-400/50 hover:border-amber-500 transition-all hover:shadow-xl">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-100 rounded-2xl mb-4">
                  <Heart className="h-7 w-7 text-amber-600" />
                </div>
                <h3 className="text-2xl font-display font-bold text-menioo-navy mb-2">
                  Soutenir Menioo
                </h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-5xl font-bold text-menioo-navy">Libre</span>
                </div>
                <p className="text-sm text-gray-500 font-medium">Don ponctuel ou mensuel</p>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-900 text-center font-medium">
                  🎁 En remerciement : accès à certaines fonctionnalités premium
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Tout le plan <strong>Gratuit</strong> inclus
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    <strong>Soutenez le développement</strong> de nouvelles fonctionnalités
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Accès à certaines <strong>fonctions premium disponibles</strong>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    <strong>Badge spécial</strong> dans la communauté
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Montant <strong>100% libre</strong> — vous choisissez
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Don <strong>ponctuel ou mensuel</strong> — vous décidez
                  </span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    <strong>Aucun engagement</strong> — annulez quand vous voulez
                  </span>
                </li>
              </ul>

              {isAuthenticated ? (
                <a
                  href="https://app.menioo.com"
                  onClick={handleAppRedirect}
                  className="block w-full bg-amber-500 text-white py-4 px-6 rounded-xl text-center font-semibold hover:bg-amber-600 transition-all shadow-sm hover:shadow-md"
                >
                  Faire un don
                </a>
              ) : (
                <a
                  href="https://app.menioo.com/signup"
                  onClick={(e) => handleSignupRedirect(e, 'donation')}
                  className="block w-full bg-amber-500 text-white py-4 px-6 rounded-xl text-center font-semibold hover:bg-amber-600 transition-all shadow-sm hover:shadow-md"
                >
                  Faire un don
                </a>
              )}
            </div>
          </div>

          {/* FEATURE COMPARISON TABLE */}
          <div className="mt-20 max-w-4xl mx-auto">
            <h2 className="text-2xl font-display font-bold text-menioo-navy text-center mb-8">
              Comparaison détaillée
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-2xl shadow-sm overflow-hidden">
                <thead>
                  <tr className="bg-menioo-navy text-white">
                    <th className="px-6 py-4 text-left font-semibold">Fonctionnalité</th>
                    <th className="px-6 py-4 text-center font-semibold">Gratuit</th>
                    <th className="px-6 py-4 text-center font-semibold bg-menioo-green">Premium</th>
                    <th className="px-6 py-4 text-center font-semibold">Don</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Recettes publiques</td>
                    <td className="px-6 py-4 text-center text-menioo-green font-medium">Illimité</td>
                    <td className="px-6 py-4 text-center text-menioo-green font-medium bg-green-50/50">Illimité</td>
                    <td className="px-6 py-4 text-center text-menioo-green font-medium">Illimité</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Recettes privées</td>
                    <td className="px-6 py-4 text-center text-amber-600 font-medium">Limitées</td>
                    <td className="px-6 py-4 text-center text-menioo-green font-medium bg-green-50/50">Illimité</td>
                    <td className="px-6 py-4 text-center text-amber-600 font-medium">Bonus</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Planification</td>
                    <td className="px-6 py-4 text-center text-amber-600 font-medium">1 semaine</td>
                    <td className="px-6 py-4 text-center text-menioo-green font-medium bg-green-50/50">Illimité</td>
                    <td className="px-6 py-4 text-center text-amber-600 font-medium">Bonus</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Liste de courses</td>
                    <td className="px-6 py-4 text-center text-amber-600 font-medium">Manuelle</td>
                    <td className="px-6 py-4 text-center text-menioo-green font-medium bg-green-50/50">Automatique</td>
                    <td className="px-6 py-4 text-center text-amber-600 font-medium">Bonus</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Import TikTok/Instagram</td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                    <td className="px-6 py-4 text-center bg-green-50/50"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Assistant IA</td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                    <td className="px-6 py-4 text-center bg-green-50/50"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Sans publicité</td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                    <td className="px-6 py-4 text-center bg-green-50/50"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Badge communauté</td>
                    <td className="px-6 py-4 text-center"><X className="h-5 w-5 mx-auto text-gray-300" /></td>
                    <td className="px-6 py-4 text-center bg-green-50/50"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-4 text-center"><Check className="h-5 w-5 mx-auto text-amber-500" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="text-3xl font-display font-bold text-menioo-navy text-center mb-12">
              Questions sur les tarifs
            </h2>
            
            <div className="space-y-3">
              {[
                {
                  question: "Puis-je changer de formule à tout moment ?",
                  answer: "Oui, vous pouvez passer du plan Gratuit au plan Premium (quand il sera disponible) à tout moment. Vous pouvez également annuler votre abonnement Premium quand vous le souhaitez, sans frais."
                },
                {
                  question: "Comment fonctionnent les dons ?",
                  answer: "Vous choisissez le montant et la fréquence (ponctuel ou mensuel). En remerciement, vous débloquez certaines fonctionnalités premium disponibles. Vous pouvez arrêter à tout moment, sans justification."
                },
                {
                  question: "Quelle différence entre un don et Premium ?",
                  answer: "Le plan Premium donne accès à TOUTES les fonctionnalités (actuelles et futures), notamment l'import IA depuis TikTok/Instagram et l'assistant personnel. Le don est un soutien libre qui débloque certaines fonctions premium en remerciement."
                },
                {
                  question: "Le plan gratuit a-t-il une durée limitée ?",
                  answer: "Non ! Le plan gratuit est disponible pour toujours, sans limite de durée. Vous pouvez l'utiliser aussi longtemps que vous le souhaitez. On croit que la version gratuite doit être utile, pas frustrante."
                },
                {
                  question: "Mes données sont-elles en sécurité ?",
                  answer: "Absolument. Connexion sécurisée via Google, données chiffrées, jamais vendues ni partagées avec des tiers. Conforme RGPD. Consultez notre politique de confidentialité pour tous les détails."
                },
                {
                  question: "Comment annuler mon abonnement ?",
                  answer: "Directement depuis votre espace personnel, en 1 clic. L'annulation prend effet à la fin de votre période en cours. Vous conservez l'accès Premium jusqu'à la fin de la période payée."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-menioo-navy pr-4">{faq.question}</span>
                    <span className={`text-menioo-green transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-20">
            <div className="bg-gradient-to-br from-menioo-navy to-menioo-navy/90 rounded-3xl shadow-2xl p-12 md:p-16 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-menioo-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-menioo-orange/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
                  Prêt à cuisiner pour de vrai ?
                </h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  Commencez gratuitement et découvrez comment Menioo transforme votre quotidien culinaire
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {isAuthenticated ? (
                    <a
                      href="https://app.menioo.com"
                      onClick={handleAppRedirect}
                      className="inline-flex items-center justify-center bg-menioo-orange text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-menioo-orange-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                      Accéder à l'app
                      <ArrowRight className="h-6 w-6 ml-2" />
                    </a>
                  ) : (
                    <>
                      <a
                        href="https://app.menioo.com/signup"
                        onClick={(e) => handleSignupRedirect(e)}
                        className="inline-flex items-center justify-center bg-menioo-orange text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-menioo-orange-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                      >
                        Commencer gratuitement
                        <ArrowRight className="h-6 w-6 ml-2" />
                      </a>
                      <Link
                        to="/"
                        className="inline-flex items-center justify-center bg-transparent border-2 border-gray-500 text-gray-300 px-10 py-5 rounded-xl font-semibold text-xl hover:border-white hover:text-white transition-all"
                      >
                        En savoir plus
                      </Link>
                    </>
                  )}
                </div>
                <p className="mt-6 text-gray-400 flex items-center justify-center text-sm">
                  <Shield className="h-4 w-4 mr-2" />
                  Sans carte bancaire &bull; Sans engagement &bull; Annulable à tout moment
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}